import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { CardOffer } from "../../../services/cards/mocks";
import { getCardOffers } from "../../../services/cards";
import { Skeleton } from "../../shared";
import { ChevronRightIcon } from "../../../icons";
import { IssueCardDrawer } from "./IssueCardDrawer";
import { pickCardTemplate } from "./CardVisual";

interface CardOffersSectionProps {
  isLoading?: boolean;
}

function CardPreview({ offer }: { offer: CardOffer }) {
  const theme = useTheme();
  const template = pickCardTemplate(
    { imageUrl: offer.imageUrl, imageUrlDark: offer.imageUrlDark },
    theme.palette.mode === "dark" ? "dark" : "light",
  );
  const hasTemplate = Boolean(template);

  return (
    <Box
      sx={{
        position: "relative",
        // Тот же форм-фактор, что и у большой карты и у превью в админке:
        // 500/295 ≈ 1.695. При width=74 высота будет ≈ 44 — визуально
        // прежний размер, только пропорция уже правильная.
        width: "74px",
        aspectRatio: "500 / 295",
        borderRadius: "8px",
        flexShrink: 0,
        overflow: "hidden",
        background: hasTemplate ? "transparent" : offer.gradient,
        backgroundImage: hasTemplate ? `url("${template}")` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {!hasTemplate && (
        <>
          <Box
            sx={{
              position: "absolute",
              right: "-10px",
              top: "-10px",
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 70%)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: "8px",
              top: "8px",
              width: "12px",
              height: "9px",
              borderRadius: "2px",
              background:
                "linear-gradient(135deg, rgba(255, 220, 130, 0.9), rgba(180, 138, 63, 0.9))",
            }}
          />
        </>
      )}
    </Box>
  );
}

function OfferCard({
  offer,
  onApply,
}: {
  offer: CardOffer;
  onApply: (offer: CardOffer) => void;
}) {
  const { t } = useTranslation("cards");
  const handleApply = () => onApply(offer);

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "20px",
        bgcolor: "secondary.main",
        padding: "16px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <CardPreview offer={offer} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}
          >
            <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
              {offer.name}
            </Typography>
            {offer.badge && (
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: "6px",
                  color: "#00CA48",
                  bgcolor: "rgba(0, 202, 72, 0.14)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {offer.badge}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              lineHeight: 1.35,
            }}
          >
            {t("offer.issue_time", {
              provider: offer.provider,
              time: offer.issueTime,
            })}
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: "13px",
          color: "text.primary",
          opacity: 0.75,
          lineHeight: 1.4,
          marginTop: "12px",
        }}
      >
        {offer.description}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          marginTop: "12px",
        }}
      >
        {offer.features.map((feature) => (
          <Box
            key={feature}
            sx={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Box
              sx={{
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                bgcolor: "primary.main",
              }}
            />
            <Typography
              sx={{
                fontSize: "12px",
                color: "text.primary",
                opacity: 0.85,
              }}
            >
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "16px",
          gap: "12px",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
            {offer.price}
          </Typography>
          {offer.priceComment && (
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                marginTop: "2px",
              }}
            >
              {offer.priceComment}
            </Typography>
          )}
        </Box>

        <ButtonBase
          onClick={handleApply}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "10px 18px",
            borderRadius: "40px",
            color: "#fff",
            fontWeight: 600,
            fontSize: "13px",
            fontFamily: "Inter",
            bgcolor: "primary.main",
            transition: "opacity 0.15s",
            "&:active": { opacity: 0.85 },
          }}
        >
          {t("apply.cta")}
          <Box sx={{ display: "flex", color: "#fff" }}>
            <ChevronRightIcon />
          </Box>
        </ButtonBase>
      </Box>
    </Box>
  );
}

export function CardOffersSection({ isLoading: parentLoading }: CardOffersSectionProps) {
  const { t } = useTranslation("cards");
  const { data, isLoading: queryLoading } = useQuery({
    queryFn: getCardOffers,
    queryKey: ["card-offers"],
  });
  const offers = data ?? [];
  const isLoading = parentLoading || queryLoading;

  const [applyOffer, setApplyOffer] = useState<CardOffer | null>(null);

  return (
    <Box>
      {isLoading ? (
        <Skeleton
          variant="rounded"
          width={170}
          height={22}
          sx={{ borderRadius: "8px", marginBottom: "14px", marginLeft: "4px" }}
        />
      ) : (
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            marginBottom: "14px",
            paddingLeft: "4px",
          }}
        >
          {t("offer.section_title")}
        </Typography>
      )}
      <Box sx={{ display: "grid", gap: "12px" }}>
        {isLoading
          ? [0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={210}
                sx={{ borderRadius: "20px" }}
              />
            ))
          : offers.length === 0
          ? (
              <Box
                sx={{
                  padding: "24px 16px",
                  borderRadius: "18px",
                  bgcolor: "secondary.main",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
                  {t("offer.empty")}
                </Typography>
              </Box>
            )
          : offers.map((offer) => (
              <OfferCard key={offer.id} offer={offer} onApply={setApplyOffer} />
            ))}
      </Box>

      <IssueCardDrawer
        open={applyOffer !== null}
        offer={applyOffer}
        onClose={() => setApplyOffer(null)}
      />
    </Box>
  );
}
